import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { withFilters } from 'coreOld/components';
import { SelectionPanelNames } from 'coreOld/constants';
import { ButtonType, FileType, SelectionOptions } from 'coreOld/enums';
import { useAppDispatch, useAppSelector, useExportHandler } from 'coreOld/hooks';
import { setFilteredCount } from 'coreOld/slices';
import { IFiltersProps } from 'coreOld/types';
import { getListPayload } from 'coreOld/utils';
import { companyPartListDefaultPayload } from 'partsOld/constants';
import { useCompanyPartsActions, useGetCompanyParts, useUomsActions } from 'partsOld/hooks';
import { mapRemoveCompanyPartsToServer } from 'partsOld/mappers';

import { columns } from './columns';
import CompanyPartsView from './CompanyPartsView';

const WAITING_TIME = 1500;

const getExportDataToSendBySelection = (selectedItems, filters, selectionMode) => {
  const companyPartsIds = selectedItems.map((item) => item.id);
  const filtersToSend =
    selectionMode === SelectionOptions.FILTERED
      ? getListPayload(filters, companyPartListDefaultPayload)?.filters
      : { id: companyPartsIds };

  return {
    fileType: FileType.XLS,
    filters: filtersToSend,
  };
};

const getExportOptions = (translator) => {
  return {
    exportInProgressMessage: translator('messageGeneratePartExportFileInProgress', {
      fileType: FileType.XLS.toUpperCase(),
    }),
    exportSuccessMessage: translator('messageGeneratePartExportSuccess', {
      fileType: FileType.XLS.toUpperCase(),
    }),
  };
};

const CompanyParts = ({ filters }: IFiltersProps) => {
  const [closeSelection, setCloseSelection] = useState(false);
  const [confirmationOption, setConfirmationOption] = useState(false);
  const [isAddCostOpen, setIsAddCostOpen] = useState(false);

  const { t } = useTranslation();
  const { handleExport } = useExportHandler();

  const dispatch = useAppDispatch();
  const { selectionMode } = useAppSelector((state) => state.table);

  const {
    records,
    count,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
  } = useGetCompanyParts(getListPayload(filters, companyPartListDefaultPayload));

  const { exportCompanyParts, removebulk } = useCompanyPartsActions();
  const { exportUomConversions } = useUomsActions();

  const removeBulkCompanyParts = async (selectedItems) => {
    await removebulk(mapRemoveCompanyPartsToServer(selectedItems, selectionMode, filters));

    setCloseSelection(true);

    setTimeout(() => setConfirmationOption(false), WAITING_TIME);
  };

  const selectOptionsConfig = (selectedItems) => [
    {
      section: [
        [
          {
            label: t('buttonLabelExportCompanyParts').toUpperCase(),
            onClick: () => {
              handleExport(
                exportCompanyParts,
                getExportDataToSendBySelection(selectedItems, filters, selectionMode),
                getExportOptions(t)
              );

              setCloseSelection(true);
            },
          },
          {
            label: t('buttonLabelExportUomConversions').toUpperCase(),
            onClick: () => {
              handleExport(
                exportUomConversions,
                getExportDataToSendBySelection(selectedItems, filters, selectionMode),
                getExportOptions(t)
              );

              setCloseSelection(true);
            },
          },
        ],
        [
          {
            label: t('buttonLabelAddCostNote').toUpperCase(),
            onClick: () => setIsAddCostOpen(true),
          },
        ],
      ],
    },
    {
      section: [
        [
          {
            label: t('buttonLabelRemove').toUpperCase(),
            onClick: () => setConfirmationOption(true),
            btnProps: {
              type: ButtonType.DANGER,
            },
          },
        ],
      ],
    },
  ];

  useEffect(() => {
    dispatch(
      setFilteredCount({
        count: count || 0,
        isLoading: isLoading || (isFetching && !isFetchingNextPage),
        isError,
      })
    );
  }, [count, isLoading, isFetching, isFetchingNextPage]);

  return (
    <CompanyPartsView
      tableColumns={columns}
      filters={filters}
      data={records}
      isError={isError}
      isLoading={isLoading || (isFetching && !isFetchingNextPage)}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      selectionType={SelectionPanelNames.PART}
      selectOptionsConfig={selectOptionsConfig}
      confirmationOption={confirmationOption}
      setConfirmationOption={setConfirmationOption}
      removeBulkCompanyParts={removeBulkCompanyParts}
      isAddCostOpen={isAddCostOpen}
      setIsAddCostOpen={setIsAddCostOpen}
      closeSelection={closeSelection}
      setCloseSelection={setCloseSelection}
    />
  );
};

export default withFilters(CompanyParts);
